import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';

const MIN_LEN = 2;
const MAX_LEN = 100;

const validateName = (name) => {
  const trimmed = (name ?? '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return { ok: false, trimmed, message: 'Project name is required.' };
  if (trimmed.length < MIN_LEN) return { ok: false, trimmed, message: `Minimum ${MIN_LEN} characters.` };
  if (trimmed.length > MAX_LEN) return { ok: false, trimmed, message: `Maximum ${MAX_LEN} characters.` };
  return { ok: true, trimmed, message: '' };
};

const CreateProjectDialog = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setServerError('');
      setSubmitting(false);
      setTouched(false);
      setSubmitted(false);
    }
  }, [open]);

  const v = useMemo(() => validateName(name), [name]);
  const canSubmit = v.ok && !submitting;

  const handleSubmit = async () => {
    setServerError('');
    setSubmitted(true); // <-- critical: trigger validation visibility

    const vv = validateName(name);
    if (!vv.ok) return;

    try {
      setSubmitting(true);
      await onCreate(vv.trimmed);
      onClose();
    } catch (err) {
      // expect onCreate to throw a human message
      setServerError(err?.message || 'Failed to create project.');
    } finally {
      setSubmitting(false);
    }
  };
  const showError = (touched || submitted) && !v.ok;
  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create project</DialogTitle>

      <DialogContent>
        {serverError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        ) : null}

        <TextField
          autoFocus
          fullWidth
          sx={{ mt: 1 }}
          label="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          error={showError}
          helperText={showError ? v.message : `${name.trim().replace(/\s+/g, ' ').length}/${MAX_LEN}`}
          inputProps={{ maxLength: MAX_LEN }}
          disabled={submitting}
          InputLabelProps={{ shrink: true }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!submitting) handleSubmit();
            }
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;
